import { useMsal } from "@azure/msal-react";
import { faMicrosoft } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Pako from "pako";
import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { combine, getToken } from "../../../../common/authMicrosoft";
import { getUniqueIdMap, seedAll } from "../../../../common/generateUniqueId";
import {
	IAuthenticateCommand,
	IFilePickerOptions,
	SPItem,
} from "../../../../common/OneDrivePicker";
import SaveFormat from "../../../../common/save/SaveFormat";
import Version from "../../../../common/Version";
import { env } from "../../../../env/env";
import {
	fetchLogicsConfiguration,
	resetLogics,
	selectFetchLogicsStatus,
} from "../../../logics/logicsSlice";
import { resetMenuManager } from "../../../menuManager/menuManagerSlice";
import {
	closeWindow,
	resetWindowManager,
} from "../../../windowManager/windowManagerSlice";
import {
	FileInfoOneDrive,
	loadEditorState,
	OneDriveLocation,
	OneDriveOpts,
	resetEditor,
	saveProjectOneDrive,
	selectEditor,
} from "../../editorSlice";
import css from "./OneDrivePicker.module.css";

export const saveVersion: Version = {
	commit: env.frontendVersion + env.branch,
	major: 1,
	minor: 0,
	patch: 0,
};

interface OneDrivePickerProps {
	modalId: string;
	disabled: boolean;
	type: "menu" | "modal";
	newName: string | null;
}

export function OneDrivePicker({
	modalId,
	disabled,
	type,
	newName,
}: OneDrivePickerProps) {
	const dispatch = useAppDispatch();
	const { instance } = useMsal();
	const { t } = useTranslation();
	const logicsStatus = useAppSelector(selectFetchLogicsStatus);
	const editor = useAppSelector(selectEditor);
	const baseUrl = "https://ltgemea.sharepoint.com";
	let win: Window | null = null;
	let port: any = null;

	const params: IFilePickerOptions = {
		sdk: "8.0",
		entry: {
			...(editor.oneDrive?.type
				? {
						...(editor.oneDrive?.type == "oneDrive"
							? {
									oneDrive: {
										files: {
											...(editor.oneDrive.location?.folder
												? {
														folder: editor.oneDrive.location?.folder,
													}
												: {}),
										},
									},
								}
							: {
									sharePoint: {
										byPath: editor.oneDrive?.location,
									},
								}),
					}
				: { oneDrive: { files: {} } }),
		},
		authentication: {},
		messaging: {
			//If timeout error, change to localhost with port
			origin: window.location.origin,
			channelId: "27",
		},
		typesAndSources: {
			mode: type == "menu" ? "folders" : "files",
			pivots: {
				oneDrive: true,
				recent: true,
				sharedLibraries: true,
				search: true,
			},
		},
		selection: {
			mode: "single",
		},
		commands: {
			pick: {
				action: "select",
				label: type == "menu" ? t("Save") : t("Select"),
			},
		},
	};

	const launchPicker = async (e: MouseEvent) => {
		e.preventDefault();
		dispatch(closeWindow(modalId));

		win = window.open("", "Picker", "width=1200,height=800");

		if (!win) return;

		const authToken = await getToken(instance, {
			resource: baseUrl,
			command: "authenticate",
			type: "SharePoint",
		});

		const queryString = new URLSearchParams({
			filePicker: JSON.stringify(params),
		});

		const url = combine(baseUrl, `_layouts/15/FilePicker.aspx?${queryString}`);

		const form = win.document.createElement("form");
		form.setAttribute("action", url);
		form.setAttribute("method", "POST");
		win.document.body.append(form);

		const input = win.document.createElement("input");
		input.setAttribute("type", "hidden");
		input.setAttribute("name", "access_token");
		input.setAttribute("value", `${authToken}`);
		form.appendChild(input);

		form.submit();

		window.addEventListener("message", (event) => {
			if (event.source && event.source === win) {
				const message = event.data;

				if (
					message.type === "initialize" &&
					message.channelId === params.messaging.channelId
				) {
					port = event.ports[0];

					port.addEventListener("message", messageListener);

					port.start();

					port.postMessage({
						type: "activate",
					});
				}
			}
		});
	};

	const handleFilePicked = async (
		pickedItem: SPItem,
		command: IAuthenticateCommand,
		location: OneDriveLocation,
		type: "oneDrive" | "sharePoint",
	) => {
		try {
			const token = await getToken(instance, command);
			const fileInfoUrl = `${pickedItem["@sharePoint.endpoint"]}/drives/${pickedItem.parentReference.driveId}/items/${pickedItem.id}`;

			// First request is necessary to get fileInfo, otherwise token will not work
			const infoResponse = await fetch(fileInfoUrl, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
			});

			const fileInfo: FileInfoOneDrive = await infoResponse.json();

			const fileContentUrl = `${fileInfo["@content.downloadUrl"]}/content`;
			const response = await fetch(fileContentUrl, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			const blob = await response.blob();

			const reader = new FileReader();
			reader.readAsArrayBuffer(blob);

			reader.onload = (event) => {
				const content = event.target?.result;

				if (!content) {
					console.warn(`Error reading file contents.`);
					return;
				}

				if (typeof content === "string") {
					console.warn(`File content read as string.`);
					return;
				}

				const json = Pako.inflate(new Uint8Array(content), {
					to: "string",
				});
				const saveFormat = JSON.parse(json) as SaveFormat;
				saveFormat.editor.blockPreview = null;
				const oneDrive: OneDriveOpts = {
					name: fileInfo.name,
					itemId: fileInfo.id,
					driveId: fileInfo.parentReference.driveId,
					lastUpdate: fileInfo.fileSystemInfo.lastModifiedDateTime,
					location,
					type,
				};
				saveFormat.editor.oneDrive = oneDrive;

				console.log({ saveFormat });

				dispatch(resetEditor());
				dispatch(resetWindowManager());
				dispatch(resetMenuManager());
				dispatch(resetLogics());

				dispatch(
					fetchLogicsConfiguration({
						...saveFormat.logicsCommitRef,
						migrate: false,
					}),
				);
				dispatch(loadEditorState(saveFormat.editor));
				seedAll(saveFormat.idMap);
			};
		} catch (error) {
			console.error("Error downloading file:", error);
		}
	};

	const messageListener = async (message: MessageEvent) => {
		switch (message.data.type) {
			case "notification":
				console.log(`notification: ${JSON.stringify(message.data.data)}`);
				break;

			case "command":
				port.postMessage({
					type: "acknowledge",
					id: message.data.id,
				});

				const command = message.data.data;

				switch (command.command) {
					case "authenticate":
						const token = await getToken(instance, command);

						if (token) {
							port.postMessage({
								type: "result",
								id: message.data.id,
								data: {
									result: "token",
									token,
								},
							});
						} else {
							console.error(
								`Could not get auth token for command: ${JSON.stringify(command)}`,
							);
						}

						break;

					case "close":
						win!.close();
						break;

					case "pick":
						const selectedItem: SPItem = command?.items[0];

						if (!selectedItem) break;

						switch (type) {
							case "modal":
								const ext = selectedItem.name.split(".").at(-1);

								if (ext != "ppf") {
									toast.error(t("invalidFormat"));
									port.postMessage({
										type: "result",
										id: message.data.id,
										data: {
											result: "success",
										},
									});
									break;
								}

								const type = command?.location.sharePoint.byPath.web.includes(
									"/sites",
								)
									? "sharePoint"
									: "oneDrive";

								handleFilePicked(
									selectedItem,
									command,
									command?.location.sharePoint.byPath,
									type,
								);
								break;
							case "menu":
								if (logicsStatus.code !== "succeeded") {
									throw new Error("Logics not loaded");
								}

								const saveFormat: SaveFormat = {
									logicsCommitRef: logicsStatus.logics.commit,
									editor,
									saveVersion,
									idMap: getUniqueIdMap(),
								};

								dispatch(
									saveProjectOneDrive({
										saveFormat,
										instance,
										t,
										notify: true,
										driveId: selectedItem.parentReference.driveId,
										itemId: selectedItem.id,
										update: false,
										newName: newName,
									}),
								);

								break;
							default:
								break;
						}

						port.postMessage({
							type: "result",
							id: message.data.id,
							data: {
								result: "success",
							},
						});

						win!.close();

						break;

					default:
						console.warn(`Unsupported command: ${JSON.stringify(command)}`, 2);

						port.postMessage({
							result: "error",
							error: {
								code: "unsupportedCommand",
								message: command.command,
							},
							isExpected: true,
						});
						break;
				}

				break;
		}
	};

	return (
		<button
			disabled={disabled}
			className={css.oneDriveButton}
			onClick={(e) => launchPicker(e)}
		>
			<FontAwesomeIcon style={{ marginRight: 4 }} icon={faMicrosoft} />
			One Drive
		</button>
	);
}
